import { automationGoalOptions } from '../data/checklists/operationsChecklist';

export const BLOG_CLASSIFICATION_PATH =
  '/blog/klassifikaciya-ii-reshenij-chat-rag-agent-harness';
export const BLOG_CLASSIFICATION_UTM =
  '?utm_source=checklist&utm_medium=organic&utm_campaign=ops_ai_classification';

export const GOAL_TO_SOLUTION_TYPE = {
  drafts: 'chat',
  actions: 'tools',
  open_research: 'deep_research',
  internal_kb: 'rag',
  multistep: 'agent',
  production: 'harness',
};

export const AI_SOLUTION_TYPES = {
  chat: {
    id: 'chat',
    title: 'Чат с LLM',
    shortDescription:
      'Диалог с моделью без доступа к вашим системам. Подходит для черновиков, обучения и быстрых ответов.',
    complexityRank: 1,
  },
  tools: {
    id: 'tools',
    title: 'LLM с инструментами',
    shortDescription:
      'Модель вызывает простые API и сервисы по запросу. Подходит для уведомлений, справок и расчётов в один-два шага.',
    complexityRank: 2,
  },
  deep_research: {
    id: 'deep_research',
    title: 'Deep Research',
    shortDescription:
      'Многошаговый поиск и синтез по открытым источникам. Для рынка, регуляторики и разведки, не для закрытой базы знаний.',
    complexityRank: 3,
  },
  rag: {
    id: 'rag',
    title: 'RAG',
    shortDescription:
      'Ответы по вашим документам и регламентам. Нужны актуальный индекс, права доступа и владелец качества базы.',
    complexityRank: 4,
  },
  agent: {
    id: 'agent',
    title: 'Агент',
    shortDescription:
      'Система сама планирует шаги и вызывает инструменты. Высокий потенциал и риск - нужны владелец процесса и обвязка.',
    complexityRank: 5,
  },
  harness: {
    id: 'harness',
    title: 'Harness',
    shortDescription:
      'Инфраструктура вокруг агента: лимиты, логи, очереди, human-in-the-loop. Для потока задач в проде, не для первого демо.',
    complexityRank: 6,
  },
};

const TYPE_ORDER = ['chat', 'tools', 'deep_research', 'rag', 'agent', 'harness'];

function typeByRank(rank) {
  return TYPE_ORDER.find((id) => AI_SOLUTION_TYPES[id].complexityRank === rank) || 'chat';
}

function goalLabels(goals) {
  const map = Object.fromEntries(automationGoalOptions.map((o) => [o.value, o.label]));
  return (goals || []).map((g) => map[g] || g);
}

function pillarIsWeak(pillarScores, pillar) {
  const p = pillarScores?.[pillar];
  if (!p || p.max <= 0) return false;
  return p.score / p.max < 0.45;
}

function readinessCapRank(totalScore, goals, pillarScores) {
  if (totalScore <= 12) return 4;
  if (totalScore <= 24) return 5;

  const wantsHarness = goals?.includes('production');
  if (wantsHarness) {
    const coreWeak =
      pillarIsWeak(pillarScores, 'data') ||
      pillarIsWeak(pillarScores, 'owner') ||
      pillarIsWeak(pillarScores, 'integrations');
    if (coreWeak) return 5;
  }
  return 6;
}

function resolveStartType(selectedTypeIds, capRank) {
  const selectedRanks = selectedTypeIds
    .map((id) => AI_SOLUTION_TYPES[id]?.complexityRank ?? 1)
    .filter(Boolean);
  const withinCap = selectedRanks.filter((r) => r <= capRank);
  if (withinCap.length > 0) {
    return typeByRank(Math.max(...withinCap));
  }
  return typeByRank(capRank);
}

function resolveTargetType(selectedTypeIds) {
  const maxRank = Math.max(
    ...selectedTypeIds.map((id) => AI_SOLUTION_TYPES[id]?.complexityRank ?? 1),
  );
  return typeByRank(maxRank);
}

/**
 * @param {{ goals: string[], totalScore: number, pillarScores?: object, readinessLabel?: string }} input
 */
export function getPilotRecommendation({ goals = [], totalScore = 0, pillarScores = null }) {
  const safeGoals = Array.isArray(goals) ? goals.filter(Boolean) : [];
  if (safeGoals.length === 0) {
    return {
      startType: null,
      targetType: null,
      isCapped: false,
      summary: 'Отметьте хотя бы один сценарий автоматизации в первом блоке чек-листа.',
      bullets: [],
      selectedLabels: [],
      blogPath: `${BLOG_CLASSIFICATION_PATH}${BLOG_CLASSIFICATION_UTM}`,
      blogUrl: `https://antologia.tech${BLOG_CLASSIFICATION_PATH}${BLOG_CLASSIFICATION_UTM}`,
    };
  }

  const selectedTypeIds = safeGoals.map((g) => GOAL_TO_SOLUTION_TYPE[g]).filter(Boolean);
  const targetType = resolveTargetType(selectedTypeIds);
  const capRank = readinessCapRank(totalScore, safeGoals, pillarScores);
  const startType = resolveStartType(selectedTypeIds, capRank);
  const targetRank = AI_SOLUTION_TYPES[targetType].complexityRank;
  const startRank = AI_SOLUTION_TYPES[startType].complexityRank;
  const isCapped = startRank < targetRank;

  const selectedLabels = goalLabels(safeGoals);
  const bullets = [];

  bullets.push(AI_SOLUTION_TYPES[startType].shortDescription);

  if (selectedLabels.length > 0) {
    bullets.push(`Вы отметили: ${selectedLabels.join('; ')}.`);
  }

  if (isCapped) {
    bullets.push(
      `По задачам это уровень «${AI_SOLUTION_TYPES[targetType].title}», но при текущей готовности (${totalScore} из 36) разумнее начать пилот с «${AI_SOLUTION_TYPES[startType].title}» и сузить контур.`,
    );
  }

  if (pillarIsWeak(pillarScores, 'data') && safeGoals.includes('internal_kb')) {
    bullets.push(
      'Слабая опора «Данные и комплаенс»: перед RAG зафиксируйте перечень данных, ПДн и правила выгрузки.',
    );
  }

  if (
    pillarIsWeak(pillarScores, 'integrations') &&
    (safeGoals.includes('actions') || safeGoals.includes('multistep') || safeGoals.includes('production'))
  ) {
    bullets.push(
      'Слабая опора «Интеграции»: опишите первый контур на проде и поддержку после POC, иначе демо не станет внедрением.',
    );
  }

  if (safeGoals.includes('production') && startType !== 'harness' && targetType === 'harness') {
    bullets.push(
      'Harness оправдан, когда есть поток задач в проде и обвязка (лимиты, логи, human-in-the-loop) - не как первый шаг пилота.',
    );
  }

  const summary = isCapped
    ? `С чего начать пилот: ${AI_SOLUTION_TYPES[startType].title}. Целевой уровень по задачам: ${AI_SOLUTION_TYPES[targetType].title}.`
    : `Рекомендуемый уровень внедрения: ${AI_SOLUTION_TYPES[startType].title}.`;

  return {
    startType,
    targetType,
    isCapped,
    summary,
    bullets: bullets.slice(0, 5),
    selectedLabels,
    blogPath: `${BLOG_CLASSIFICATION_PATH}${BLOG_CLASSIFICATION_UTM}`,
    blogUrl: `https://antologia.tech${BLOG_CLASSIFICATION_PATH}${BLOG_CLASSIFICATION_UTM}`,
  };
}

export function formatPilotRecommendationReportSection(recommendation) {
  if (!recommendation?.startType) {
    return `\nРЕКОМЕНДАЦИЯ ПО УРОВНЮ ВНЕДРЕНИЯ\n${recommendation?.summary || '-'}\n`;
  }
  const target = AI_SOLUTION_TYPES[recommendation.targetType];
  let text = `
══════════════════════════════════════════════════════════════
РЕКОМЕНДАЦИЯ ПО УРОВНЮ ВНЕДРЕНИЯ
══════════════════════════════════════════════════════════════
${recommendation.summary}
`;
  if (recommendation.isCapped && target) {
    text += `Целевой уровень по отмеченным задачам: ${target.title}\n`;
  }
  recommendation.bullets.forEach((b) => {
    text += `- ${b}\n`;
  });
  text += `Статья: ${recommendation.blogUrl}\n`;
  return text;
}
